import {
    Box,
    Button,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    IconButton,
    Badge,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    FormControl,
    FormLabel,
    Input,
    VStack,
    useToast,
    Spinner,
    Text,
    Tag,
    TagLabel,
    TagCloseButton,
    HStack,
    Wrap,
    WrapItem,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { FiEdit, FiTrash2, FiPlus } from "react-icons/fi";
import axios from "axios";
import { api_url } from "../../url";

export default function FeaturedSectionManager() {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [games, setGames] = useState([]);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedSection, setSelectedSection] = useState(null);
    const toast = useToast();

    const [formData, setFormData] = useState({
        title: "",
        games: [],
        status: true,
        order: 0,
    });

    const [gameSearch, setGameSearch] = useState("");

    const fetchSections = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const { data } = await axios.get(`${api_url}/frontend/featured-sections`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (data.success) {
                setSections(data.data);
            }
        } catch (error) {
            console.error("Failed to fetch sections", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchGames = async () => {
        try {
            const token = localStorage.getItem("token");
            const { data } = await axios.get(`${api_url}/games?limit=1000`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (data.success) {
                setGames(data.data);
            }
        } catch (error) {
            console.error("Failed to fetch games", error);
        }
    };

    useEffect(() => {
        fetchSections();
        fetchGames();
    }, []);

    const handleOpenModal = (section = null) => {
        if (section) {
            setSelectedSection(section);
            setFormData({
                title: section.title || "",
                games: section.games?.map(g => g._id || g) || [],
                status: section.status ?? true,
                order: section.order || 0,
            });
        } else {
            setSelectedSection(null);
            setFormData({
                title: "",
                games: [],
                status: true,
                order: 0,
            });
        }
        onOpen();
    };

    const handleSubmit = async () => {
        try {
            const token = localStorage.getItem("token");
            let res;
            if (selectedSection) {
                res = await axios.put(`${api_url}/frontend/featured-sections/${selectedSection._id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            } else {
                res = await axios.post(`${api_url}/frontend/featured-sections`, formData, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }

            if (res.data.success) {
                toast({
                    title: selectedSection ? "Section Updated" : "Section Created",
                    status: "success",
                    duration: 3000,
                });
                fetchSections();
                onClose();
            }
        } catch (error) {
            toast({
                title: "Operation Failed",
                description: error.response?.data?.message || error.message,
                status: "error",
            });
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            const token = localStorage.getItem("token");
            await axios.delete(`${api_url}/frontend/featured-sections/${id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            toast({ title: "Deleted", status: "success" });
            fetchSections();
        } catch (error) {
            toast({ title: "Delete Failed", status: "error" });
        }
    };

    const addGameToSection = (gameId) => {
        if (!gameId || typeof gameId !== "string" || gameId.trim() === "") {
            toast({ title: "Invalid Game ID", status: "warning" });
            return;
        }
        if (!formData.games.includes(gameId)) {
            setFormData({ ...formData, games: [...formData.games, gameId] });
        }
        setGameSearch("");
    };

    const removeGameFromSection = (gameId) => {
        setFormData({ ...formData, games: formData.games.filter(id => id !== gameId) });
    };

    const filteredGames = gameSearch
        ? games.filter(g => g.name.toLowerCase().includes(gameSearch.toLowerCase())).slice(0, 10)
        : [];

    return (
        <Box>
            <Flex justify="flex-end" mb={4}>
                <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={() => handleOpenModal()}>
                    Add New Section
                </Button>
            </Flex>

            {loading ? (
                <Flex justify="center" py={10}>
                    <Spinner />
                </Flex>
            ) : (
                <Table variant="simple">
                    <Thead bg="gray.50">
                        <Tr>
                            <Th>Title</Th>
                            <Th>Games Count</Th>
                            <Th>Order</Th>
                            <Th>Status</Th>
                            <Th>Actions</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {sections.map((s) => (
                            <Tr key={s._id}>
                                <Td fontWeight="600">{s.title}</Td>
                                <Td>{s.games?.length || 0} games</Td>
                                <Td>{s.order}</Td>
                                <Td>
                                    <Badge colorScheme={s.status ? "green" : "red"}>{s.status ? "Active" : "Inactive"}</Badge>
                                </Td>
                                <Td>
                                    <Flex gap={2}>
                                        <IconButton size="sm" icon={<FiEdit />} onClick={() => handleOpenModal(s)} />
                                        <IconButton size="sm" icon={<FiTrash2 />} onClick={() => handleDelete(s._id)} colorScheme="red" variant="ghost" />
                                    </Flex>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            )}

            {/* Modal */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{selectedSection ? "Edit Section" : "Add New Section"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={4} align="stretch">
                            <FormControl isRequired>
                                <FormLabel>Section Title</FormLabel>
                                <Input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Popular Games" />
                            </FormControl>

                            <FormControl>
                                <FormLabel>Select Games</FormLabel>
                                <Flex gap={2} mb={2}>
                                    <Input
                                        placeholder="Type to search games..."
                                        value={gameSearch}
                                        onChange={(e) => setGameSearch(e.target.value)}
                                    />
                                    <Input
                                        placeholder="Or enter Game ID"
                                        width="180px"
                                        id="directGameIdInput"
                                    />
                                    <Button
                                        colorScheme="green"
                                        size="md"
                                        onClick={() => {
                                            const id = document.getElementById('directGameIdInput').value;
                                            if (id) {
                                                addGameToSection(id);
                                                document.getElementById('directGameIdInput').value = '';
                                            }
                                        }}
                                    >
                                        Add
                                    </Button>
                                </Flex>
                                {filteredGames.length > 0 && (
                                    <Box mt={2} p={2} border="1px solid #eee" borderRadius="md" bg="white" boxShadow="sm" maxHeight="200px" overflowY="auto">
                                        {filteredGames.map(g => (
                                            <Flex
                                                key={g._id}
                                                p={2}
                                                _hover={{ bg: "blue.50" }}
                                                cursor="pointer"
                                                onClick={() => addGameToSection(g._id)}
                                                justify="space-between"
                                                align="center"
                                                fontSize="sm"
                                            >
                                                <Box>
                                                    <Text fontWeight="bold">{g.name}</Text>
                                                    <Text fontSize="xs" color="gray.500">{g.provider?.name}</Text>
                                                </Box>
                                                <Badge fontSize="10px">{g._id}</Badge>
                                            </Flex>
                                        ))}
                                    </Box>
                                )}
                            </FormControl>

                            <Box>
                                <Text fontSize="sm" fontWeight="bold" mb={2}>Selected Games ({formData.games.length}):</Text>
                                <Wrap>
                                    {formData.games.map(gameId => {
                                        const gameInfo = games.find(g => g._id === gameId);
                                        return (
                                            <WrapItem key={gameId}>
                                                <Tag size="md" variant="solid" colorScheme="blue">
                                                    <TagLabel>{gameInfo?.name || "Unknown"}</TagLabel>
                                                    <TagCloseButton onClick={() => removeGameFromSection(gameId)} />
                                                </Tag>
                                            </WrapItem>
                                        );
                                    })}
                                </Wrap>
                            </Box>

                            <Flex gap={4}>
                                <FormControl>
                                    <FormLabel>Order</FormLabel>
                                    <Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} />
                                </FormControl>
                                <FormControl display="flex" alignItems="center" pt={8}>
                                    <Badge colorScheme={formData.status ? "green" : "red"} px={2} borderRadius="md">
                                        {formData.status ? "Active" : "Inactive"}
                                    </Badge>
                                    <Button
                                        size="xs"
                                        ml={2}
                                        onClick={() => setFormData({ ...formData, status: !formData.status })}
                                    >
                                        Toggle
                                    </Button>
                                </FormControl>
                            </Flex>
                        </VStack>
                    </ModalBody>

                    <ModalFooter>
                        <Button variant="ghost" mr={3} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleSubmit}>
                            Save Section
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}
